/**
 * Acesso negado.
 *
 * É para onde os botões com cadeado levam. Não é uma tela de erro: a pessoa
 * tocou numa ação que existe, que ela vê, e que alguém pode liberar. O que
 * ela precisa saber cabe em três blocos — o que faltou (com o papel atual e o
 * nome da permissão), o que ela AINDA pode fazer, e a quem pedir.
 *
 * A lista do que ainda pode vem das permissões efetivas reais, vindas do
 * servidor; nada aqui é inventado. "Pedir a quem administra" leva ao grupo,
 * onde estão as pessoas que podem liberar — sem canal externo até o
 * compartilhador existir no produto.
 */
import { ROLE_LABELS } from '@nv/core';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { MembroDaViagem } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Card, SectionLabel } from '../../ui/primitives';
import { descricaoDaAcao, permissaoDaBusca, ROTULOS_PERMISSAO } from './permissaoLabels';

export function NegadoScreen({
  membros,
  meuMemberId,
  permissoes,
}: {
  membros: MembroDaViagem[];
  meuMemberId: string;
  permissoes: string[];
}) {
  const [busca] = useSearchParams();
  const navegar = useNavigate();

  const chave = permissaoDaBusca(busca.get('permissao'));
  const papel = membros.find((m) => m.memberId === meuMemberId)?.role;

  // O que continua liberado, na ordem em que aparece no dia a dia.
  const aindaPode = [
    permissoes.includes('edit_itinerary') ? 'Criar e editar atividades' : null,
    'Concluir e reabrir atividades',
    permissoes.includes('create_expenses') ? 'Registrar despesas' : null,
  ].filter((x): x is string => x !== null);

  return (
    <section className="screen negado">

      <span className="negado__icone" aria-hidden="true">
        <Icon name="semPermissao" size={30} />
      </span>

      <h1 className="negado__titulo">Você não pode {descricaoDaAcao(chave)}</h1>

      <p className="negado__texto">
        {papel && (
          <>
            Nesta viagem você é <b>{ROLE_LABELS[papel]}</b>.{' '}
          </>
        )}
        {chave ? (
          <>
            Isso depende da permissão <b>{ROTULOS_PERMISSAO[chave]}</b>, que hoje não está
            liberada para você.
          </>
        ) : (
          <>Essa ação depende de uma permissão que hoje não está liberada para você.</>
        )}
      </p>

      <Card>
        <SectionLabel>O que você ainda pode fazer</SectionLabel>
        <ul className="negado__lista">
          {aindaPode.map((item) => (
            <li className="negado__item" key={item}>
              <Icon name="concluir" size={18} />
              {item}
            </li>
          ))}
        </ul>
      </Card>

      <button
        type="button"
        className="nv-btn nv-btn--primary nv-btn--block"
        onClick={() => navegar('/grupo')}
      >
        <Icon name="pessoas" size={20} />
        Pedir a quem administra
      </button>

      <button
        type="button"
        className="nv-btn nv-btn--tertiary nv-btn--block"
        onClick={() => navegar(-1)}
      >
        Voltar
      </button>
    </section>
  );
}
